// Mirror of CssfPdfLanguageSkillsSection.jsx (forced French): bar title, native
// languages row, then an "other languages" table. The PDF lays the language
// names in a separate left column aligned to table rows via a spacer; here we
// fold the name into the table's first column so alignment is guaranteed while
// looking the same (name right-aligned in teal, levels centered).

import { AlignmentType, BorderStyle, Table, TableCell, TableRow, VerticalAlign, WidthType } from "docx";
import { getNativeLanguageCodes } from "../../../../utils/getNativeLanguageCodes";
import { barSectionTitle, frac, NO_CELL_BORDERS, NO_TABLE_BORDERS, para, run, twoColumnRow } from "../../shared/wordHelpers";
import { CONTENT_WIDTH } from "../../shared/wordHelpers";
import { CSSF_ACCENT, CSSF_RULE, CSSF_TEXT, FONT_VERDANA } from "../../shared/wordTheme";
import { pxToPt, pxToTwips } from "../../shared/wordUnits";

const DOC_LANG = "fr";
const FRACTIONS = [ 0.30, 0.14, 0.14, 0.14, 0.14, 0.14 ];
const COLS = FRACTIONS.map( frac );

function spanWidth( span ) {
    return COLS.slice( 0, span ).reduce( ( a, b ) => a + b, 0 );
}

function tableCell( paragraph, { span = 1, rule, valign, widthOverride } = {} ) {
    return new TableCell( {
        columnSpan: span,
        width: { size: widthOverride ?? spanWidth( span ), type: WidthType.DXA },
        verticalAlign: valign,
        borders: rule
            ? { ...NO_CELL_BORDERS, bottom: { style: BorderStyle.SINGLE, size: 6, color: CSSF_RULE } }
            : NO_CELL_BORDERS,
        children: [ paragraph ],
    } );
}

function headerLabel( text ) {
    return para( run( text, { pt: pxToPt( 7 ), color: CSSF_ACCENT, font: FONT_VERDANA, allCaps: true } ), { alignment: AlignmentType.CENTER, spacing: { after: 0 } } );
}

function subLabel( text ) {
    return para( run( text, { pt: pxToPt( 7 ), color: CSSF_ACCENT, font: FONT_VERDANA } ), { alignment: AlignmentType.CENTER, spacing: { after: 0 } } );
}

function levelCell( text ) {
    return tableCell(
        para( run( text, { pt: pxToPt( 9 ), color: CSSF_TEXT, font: FONT_VERDANA } ), { alignment: AlignmentType.CENTER, spacing: { after: 0 } } ),
        { rule: true }
    );
}

export function buildCssfLanguages( { data } ) {
    const { languageSkills } = data;
    const nativeCodes = getNativeLanguageCodes( languageSkills );

    const elements = [ barSectionTitle( "LANGUES", { accent: CSSF_ACCENT, font: FONT_VERDANA } ) ];

    const nativeList = languageSkills
        .filter( skill => skill.language )
        .filter( skill => nativeCodes.includes( skill.language.code ) )
        .sort( ( a, b ) => a.order - b.order )
        .map( skill => skill.language.translations[ DOC_LANG ] )
        .join( ", " );

    elements.push( twoColumnRow(
        [ para( run( "Langue(s) maternelle(s)", { pt: 9, color: CSSF_ACCENT, font: FONT_VERDANA } ), { alignment: AlignmentType.RIGHT, spacing: { before: pxToTwips( 4 ), after: 0 } } ) ],
        [ para( run( nativeList, { pt: 9, color: CSSF_TEXT, font: FONT_VERDANA } ), { spacing: { before: pxToTwips( 4 ), after: 0 } } ) ],
        { rightMarginLeftPx: 12 }
    ) );

    const others = languageSkills
        .filter( skill => skill.language )
        .filter( skill => !nativeCodes.includes( skill.language.code ) )
        .sort( ( a, b ) => a.order - b.order );

    const nameCell = text => tableCell(
        para( run( text, { pt: 9, color: CSSF_ACCENT, font: FONT_VERDANA } ), { alignment: AlignmentType.RIGHT, spacing: { after: 0 } } ),
        { valign: VerticalAlign.CENTER }
    );

    const groupHeaderRow = new TableRow( {
        children: [
            nameCell( "Autre(s) langue(s)" ),
            tableCell( headerLabel( "COMPRENDRE" ), { span: 2, rule: true } ),
            tableCell( headerLabel( "PARLER" ), { span: 2, rule: true } ),
            tableCell( headerLabel( "ÉCRIRE" ), { span: 1, rule: true } ),
        ],
    } );

    const subHeaderRow = new TableRow( {
        children: [
            tableCell( para( [], { spacing: { after: 0 } } ) ),
            tableCell( subLabel( "Écouter" ), { rule: true } ),
            tableCell( subLabel( "Lire" ), { rule: true } ),
            tableCell( subLabel( "Prendre part à une conversation" ), { rule: true } ),
            tableCell( subLabel( "S'exprimer oralement en continu" ), { rule: true } ),
            tableCell( para( [], { spacing: { after: 0 } } ), { rule: true } ),
        ],
    } );

    const dataRows = others.map( skill => {
        const level = key => skill[ key ]?.translations?.[ DOC_LANG ] ?? "";
        return new TableRow( {
            children: [
                nameCell( skill.language.translations[ DOC_LANG ] ),
                levelCell( level( "listeningComprehensionLevel" ) ),
                levelCell( level( "readingComprehensionLevel" ) ),
                levelCell( level( "oralInteractionLevel" ) ),
                levelCell( level( "continuousOralExpressionLevel" ) ),
                levelCell( level( "writingExpressionLevel" ) ),
            ],
        } );
    } );

    elements.push( new Table( {
        width: { size: CONTENT_WIDTH, type: WidthType.DXA },
        columnWidths: COLS,
        borders: NO_TABLE_BORDERS,
        rows: [ groupHeaderRow, subHeaderRow, ...dataRows ],
    } ) );

    return elements;
}
